import React, { useEffect, useState } from 'react';
import { Link, NavLink, useParams } from 'react-router-dom';
import { HELP_CATEGORIES, HELP_ARTICLES } from './helpContent';

/**
 * Public Help Center. Rendered inside PublicLayout (marketing nav + footer).
 * Routes:
 *   /help          -> overview (category cards)
 *   /help/:slug    -> a single article, with the sidebar for navigation
 * All colors resolve from the active [data-theme] tokens (text-white / gray-*).
 */

const catTitle = (slug) => {
  const c = HELP_CATEGORIES.find((cat) => cat.articles.includes(slug));
  return c ? c.title : '';
};

/** A screenshot with a graceful placeholder until the image exists in /help. */
const Shot = ({ src, alt, caption }) => {
  const [failed, setFailed] = useState(false);
  return (
    <figure className="mt-6">
      {failed ? (
        <div
          className="flex items-center justify-center rounded-xl border border-dashed border-gray-600 bg-gray-800/40 text-center px-6 py-12"
          role="img"
          aria-label={alt}
        >
          <div>
            <svg className="mx-auto h-8 w-8 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <rect x="3" y="4" width="18" height="14" rx="2" />
              <path d="M3 15l5-4 4 3 3-2 6 5" />
              <circle cx="8.5" cy="8.5" r="1.5" />
            </svg>
            <p className="mt-3 text-sm text-gray-400">{alt}</p>
            <p className="mt-1 text-xs text-gray-500">Screenshot coming soon</p>
          </div>
        </div>
      ) : (
        <img
          src={`/help/${src}`}
          alt={alt}
          loading="lazy"
          onError={() => setFailed(true)}
          className="w-full rounded-xl border border-gray-700 shadow-sm-glow"
        />
      )}
      {caption && <figcaption className="mt-2 text-center text-xs text-gray-500">{caption}</figcaption>}
    </figure>
  );
};

const Block = ({ b }) => {
  switch (b.type) {
    case 'h':
      return <h2 className="mt-8 text-xl font-semibold text-white">{b.text}</h2>;
    case 'p':
      return <p className="mt-4 text-gray-300 leading-relaxed">{b.text}</p>;
    case 'steps':
      return (
        <ol className="mt-4 space-y-2 text-gray-300 list-decimal pl-5 marker:text-gray-500">
          {b.items.map((it) => <li key={it} className="pl-1 leading-relaxed">{it}</li>)}
        </ol>
      );
    case 'list':
      return (
        <ul className="mt-4 space-y-2 text-gray-300 list-disc pl-5 marker:text-gray-600">
          {b.items.map((it) => <li key={it} className="pl-1 leading-relaxed">{it}</li>)}
        </ul>
      );
    case 'tip':
      return (
        <div className="mt-6 rounded-xl border border-blue-500/40 bg-blue-500/10 px-4 py-3 text-sm text-blue-100">
          <span className="font-semibold">Tip:</span> {b.text}
        </div>
      );
    case 'code':
      return (
        <pre className="mt-6 overflow-x-auto rounded-xl border border-gray-700 bg-gray-900/80 p-4 text-xs text-gray-200">
          <code>{b.text}</code>
        </pre>
      );
    case 'shot':
      return <Shot src={b.src} alt={b.alt} caption={b.caption} />;
    default:
      return null;
  }
};

const Sidebar = ({ activeSlug }) => (
  <nav aria-label="Help topics" className="space-y-6">
    {HELP_CATEGORIES.map((cat) => (
      <div key={cat.id}>
        <p className="px-3 text-xs font-semibold uppercase tracking-wide text-gray-500">{cat.title}</p>
        <ul className="mt-2 space-y-0.5">
          {cat.articles.map((slug) => (
            <li key={slug}>
              <NavLink
                to={`/help/${slug}`}
                className={({ isActive }) =>
                  `block rounded-lg px-3 py-2 text-sm transition-colors ${
                    isActive || slug === activeSlug
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-400 hover:bg-gray-800/60 hover:text-white'
                  }`
                }
              >
                {HELP_ARTICLES[slug]?.title || slug}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    ))}
  </nav>
);

const Overview = () => (
  <div>
    <div className="text-center max-w-2xl mx-auto">
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white">Help Center</h1>
      <p className="mt-4 text-lg text-gray-400">
        Everything you need to set up your knowledge base, build your Site Agent, and get the most out of SageMindLabs.
      </p>
    </div>
    <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {HELP_CATEGORIES.map((cat) => (
        <div key={cat.id} className="sm-card-static p-6">
          <h2 className="text-lg font-semibold text-white">{cat.title}</h2>
          <ul className="mt-4 space-y-2">
            {cat.articles.map((slug) => (
              <li key={slug}>
                <Link to={`/help/${slug}`} className="text-sm text-gray-300 hover:text-white">
                  {HELP_ARTICLES[slug]?.title || slug}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </div>
);

const HelpCenter = () => {
  const { slug } = useParams();
  const article = slug ? HELP_ARTICLES[slug] : null;

  useEffect(() => {
    document.title = article
      ? `${article.title} | SageMindLabs Help`
      : 'Help Center | SageMindLabs';
  }, [article]);

  // /help overview
  if (!slug) {
    return (
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-16">
        <Overview />
      </div>
    );
  }

  // Unknown article
  if (!article) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-24 text-center">
        <h1 className="text-2xl font-bold text-white">Article not found</h1>
        <p className="mt-3 text-gray-400">That help topic doesn’t exist (yet).</p>
        <Link to="/help" className="sm-primary-button mt-6 inline-flex text-sm">Back to Help Center</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-12">
      <div className="grid gap-10 lg:grid-cols-[16rem_1fr]">
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <Link to="/help" className="mb-4 inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white">
            <span aria-hidden="true">←</span> Help Center
          </Link>
          <Sidebar activeSlug={slug} />
        </aside>

        <article className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{catTitle(slug)}</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-white">{article.title}</h1>
          {article.summary && <p className="mt-3 text-lg text-gray-400 leading-relaxed">{article.summary}</p>}
          <div className="mt-2">
            {article.body.map((b, i) => <Block key={i} b={b} />)}
          </div>

          <div className="mt-12 border-t border-gray-800 pt-6">
            <p className="text-sm text-gray-400">
              Still stuck? <Link to="/pricing" className="text-white underline decoration-gray-600 hover:decoration-white">Talk to us</Link> or head back to the{' '}
              <Link to="/help" className="text-white underline decoration-gray-600 hover:decoration-white">Help Center</Link>.
            </p>
          </div>
        </article>
      </div>
    </div>
  );
};

export default HelpCenter;
